import { useRef, useState } from 'react';
import { resolveAssetUrl } from '../api/client';
import { useLanguage, pick } from '../lib/language';
import type { Parameters } from '../types/parameters';
import { DownloadBar } from './DownloadBar';
import { LoupePreview } from './LoupePreview';
import {
  IconZoom,
  IconClose,
  IconGrid,
  IconEyedropper,
  IconSplit,
  IconAnnotate,
} from './Icon';
import { useElapsed } from '../lib/use-elapsed';

const TB_BASE =
  'inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border backdrop-blur motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent';
const TB_IDLE = 'border-white/20 bg-black/40 text-white hover:bg-black/55';
const TB_ACTIVE = 'border-accent bg-accent/20 text-accent hover:bg-accent/30';
const TB_DISABLED = 'disabled:cursor-not-allowed disabled:opacity-40';

/** Compact toolbar button factory — used by the image toolbar so the
 *  repeated className soup lives in one place. */
function TBButton({
  active, disabled, label, onClick, testId, children,
}: {
  active: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={disabled}
      data-testid={testId}
      className={`${TB_BASE} ${active ? TB_ACTIVE : TB_IDLE}${disabled ? ' ' + TB_DISABLED : ''}`}
    >
      {children}
    </button>
  );
}

export interface EyePreviewProps {
  /**
   * URL of the most recently generated eye image. Optional — the preview
   * shows a placeholder when there is no image yet, then becomes the
   * returned AI image after the first generation completes.
   */
  imageUrl?: string | null;
  /** Shows a "Generating…" overlay while the request is in flight. */
  generating?: boolean;
  /**
   * Current parameter set, used to derive a meaningful alt text for the
   * generated image so screen-reader users hear the eye's features
   * (iris pattern, catchlight state, lid shape) rather than a generic
   * placeholder.
   */
  parameters?: Parameters | null;
  /**
   * v5 — id of the generated image. Used by the inline DownloadBar so
   * mum can download the canonical PNG without leaving the Studio.
   */
  imageId?: string | null;
  /**
   * v5 — fired when "Set as starting point" is clicked on the inline
   * DownloadBar. Wired by Studio.tsx to load this eye as the next
   * canonical reference (chat.setAsStartingPoint).
   */
  onSetAsStarting?: () => void;
  /**
   * v7 — fired when the eyedropper button is clicked. Parent flips the
   * armed state and registers a global click listener (see ColorPicker).
   */
  onEyedropperToggle?: () => void;
  /**
   * v7 — true when the eyedropper is currently armed (the next click
   * anywhere on the page will sample a pixel). Drives the toolbar button's
   * pressed / unpressed state.
   */
  eyedropperArmed?: boolean;
  /**
   * v7 — fired when the "Compare with previous" / "Diff with parent"
   * button is clicked. Studio.tsx opens the before/after overlay.
   */
  onCompareToggle?: () => void;
  /**
   * v7 — true when the before/after overlay is open. Used to disable the
   * toolbar button (so clicking again closes it instead of staying open).
   */
  compareOpen?: boolean;
  /**
   * v7 — fired when the "Annotate" / "Annotation layer" button is clicked.
   * Studio.tsx flips the annotate-mode flag.
   */
  onAnnotateToggle?: () => void;
  /**
   * v7 — true while annotation mode is active.
   */
  annotateArmed?: boolean;
  /**
   * v7 — disable compare + annotate buttons when there's no parent (first
   * generation has nothing to compare against).
   */
  hasParent?: boolean;
  /**
   * v7 — ref-callback that exposes the underlying <img> element so the
   * Studio can plug it into ColorPicker.eyedropperImage when the user
   * arms the eyedropper.
   */
  imageElementRef?: (el: HTMLImageElement | null) => void;
}

/**
 * Real-image-only preview area. Every eye image shown to the user comes
 * from the backend's image-generation pipeline — there is no procedural
 * fallback, no inline SVG, no fake eye. When a generation is in flight,
 * the previous image is softly blurred behind a centred spinner so the
 * user can see work-in-progress without losing sight of the reference
 * they were iterating on.
 *
 * v6 — adds a grid-overlay toggle next to the loupe toggle. Two image
 * toolbar buttons: zoom-into-the-eye (existing) and show-alignment-guides
 * (new). The grid draws rule-of-thirds lines + a centre crosshair on
 * top of the image with `pointer-events: none` so it never blocks
 * interaction.
 *
 * v7 — adds three more toolbar buttons:
 *   - Eyedropper (pixel colour picker on the image)
 *   - Compare-with-previous (before/after slider overlay)
 *   - Annotate (drop hotspots on the iris)
 * Disabled when there's no image or no parent (compare/annotate).
 */
export function EyePreview(props: EyePreviewProps) {
  const {
    imageUrl,
    generating,
    parameters,
    imageId,
    onSetAsStarting,
    onEyedropperToggle,
    eyedropperArmed,
    onCompareToggle,
    compareOpen,
    onAnnotateToggle,
    annotateArmed,
    hasParent,
    imageElementRef,
  } = props;
  const resolved = imageUrl ? resolveAssetUrl(imageUrl) : null;
  const altText = describeEye(parameters ?? null);
  const { lang } = useLanguage();

  // v5 — loupe toggle. Local state owned by EyePreview so callers don't
  // need to know about it. Resets when the image swaps.
  const [loupeOn, setLoupeOn] = useState(false);
  // v6 — grid overlay toggle (rule of thirds + centre crosshair).
  const [gridOn, setGridOn] = useState(false);

  // v7 — internal ref mirror so the parent callback gets the latest value.
  const localImgRef = useRef<HTMLImageElement | null>(null);

  // v6-polish — elapsed seconds for the loading overlay. Re-renders the
  // overlay once per second while a generation is in flight.
  const elapsed = useElapsed(Boolean(generating));

  const gridLabel = pick(lang, {
    plain: gridOn ? 'Hide alignment guides' : 'Show alignment guides',
    technical: gridOn
      ? 'Hide grid overlay (rule of thirds)'
      : 'Grid overlay (rule of thirds)',
  });
  const eyedropLabel = pick(lang, {
    plain: eyedropperArmed ? 'Eyedropper armed — click on the eye (Esc to cancel)' : 'Pick a colour from the eye',
    technical: eyedropperArmed
      ? 'Eyedropper active — next click samples a pixel (Esc cancels)'
      : 'Eyedropper tool',
  });
  const compareLabel = pick(lang, {
    plain: compareOpen ? 'Close compare with previous' : 'Compare with previous',
    technical: compareOpen
      ? 'Close diff overlay'
      : 'Diff with parent (before/after slider)',
  });
  const annotateLabel = pick(lang, {
    plain: annotateArmed ? 'Stop annotating' : 'Annotate this eye',
    technical: annotateArmed
      ? 'Exit annotation layer'
      : 'Annotation layer (drop hotspots)',
  });

  return (
    <div
      data-testid="eye-preview-root"
      className="relative w-full overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-900 text-neutral-100 shadow-sm dark:border-neutral-800"
    >
      {/* v5/v6/v7 — Image toolbar. Loupe + grid + eyedropper + compare + annotate.
          All 44×44 buttons, only visible when we have an image. */}
      {resolved ? (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
          {onAnnotateToggle ? (
            <TBButton
              active={Boolean(annotateArmed)}
              disabled={!hasParent && !annotateArmed}
              label={annotateLabel}
              onClick={() => onAnnotateToggle()}
              testId="eye-preview-annotate-toggle"
            >
              <IconAnnotate size={16} aria-hidden />
            </TBButton>
          ) : null}
          {onCompareToggle ? (
            <TBButton
              active={Boolean(compareOpen)}
              disabled={!hasParent}
              label={compareLabel}
              onClick={() => onCompareToggle()}
              testId="eye-preview-compare-toggle"
            >
              <IconSplit size={16} aria-hidden />
            </TBButton>
          ) : null}
          {onEyedropperToggle ? (
            <TBButton
              active={Boolean(eyedropperArmed)}
              label={eyedropLabel}
              onClick={() => onEyedropperToggle()}
              testId="eye-preview-eyedropper-toggle"
            >
              <IconEyedropper size={16} aria-hidden />
            </TBButton>
          ) : null}
          <TBButton
            active={gridOn}
            label={gridLabel}
            onClick={() => setGridOn((v) => !v)}
            testId="eye-preview-grid-toggle"
          >
            <IconGrid size={16} aria-hidden />
          </TBButton>
          <TBButton
            active={loupeOn}
            label={loupeOn ? 'Exit zoom' : 'Zoom into the eye'}
            onClick={() => setLoupeOn((v) => !v)}
            testId="eye-preview-loupe-toggle"
          >
            {loupeOn ? <IconClose size={16} aria-hidden /> : <IconZoom size={16} aria-hidden />}
          </TBButton>
        </div>
      ) : null}

      <div className="relative aspect-[16/10] w-full bg-gradient-to-br from-neutral-900 via-neutral-950 to-black">
        {resolved && loupeOn ? (
          // v5 — loupe: square crop centred on iris + 1×–4× zoom slider.
          <div className="absolute inset-0 flex items-center justify-center p-2">
            <LoupePreview imageUrl={resolved} alt={altText} />
          </div>
        ) : resolved ? (
          <>
            <img
              ref={(el) => {
                localImgRef.current = el;
                imageElementRef?.(el);
              }}
              src={resolved}
              alt={altText}
              crossOrigin="anonymous"
              className={[
                'absolute inset-0 h-full w-full object-cover motion-safe:duration-300 motion-safe:transition-[filter]',
                generating
                  ? 'motion-safe:scale-[1.02] blur-[4px]'
                  : 'scale-100 blur-0',
                compareOpen || annotateArmed
                  ? 'pointer-events-none opacity-40'
                  : '',
              ].join(' ')}
              draggable={false}
            />
            {gridOn ? (
              // v6 — alignment grid overlay. `pointer-events: none` so it
              // never blocks interaction with the image (zoom, drag, etc.).
              <svg
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {/* rule-of-thirds grid */}
                <g
                  fill="none"
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth="0.18"
                  strokeDasharray="0.6 0.6"
                >
                  <line x1="33.333" y1="0" x2="33.333" y2="100" />
                  <line x1="66.666" y1="0" x2="66.666" y2="100" />
                  <line x1="0" y1="33.333" x2="100" y2="33.333" />
                  <line x1="0" y1="66.666" x2="100" y2="66.666" />
                </g>
                {/* centre crosshair */}
                <g
                  fill="none"
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth="0.25"
                >
                  <line x1="50" y1="46" x2="50" y2="54" />
                  <line x1="46" y1="50" x2="54" y2="50" />
                  <circle cx="50" cy="50" r="0.6" />
                </g>
              </svg>
            ) : null}
            {/* v7 — annotate-mode hint overlay so users know clicks drop
                hotspots on the iris. Plain mode keeps the friendly copy;
                technical mode gets the terse version. */}
            {annotateArmed ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-end justify-center pb-4"
              >
                <span className="rounded-md border border-accent bg-accent/20 px-3 py-1 text-xs font-semibold text-accent backdrop-blur">
                  {pick(lang, {
                    plain: 'Click on the eye to add a note',
                    technical: 'Annotation layer armed — click to drop a hotspot',
                  })}
                </span>
              </div>
            ) : null}
          </>
        ) : (
          <div
            role="img"
            aria-label="Waiting for the first eye generation to complete"
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center text-neutral-400"
          >
            <span aria-hidden="true" className="text-3xl">
              👁️
            </span>
            <span className="text-sm font-medium">No reference yet</span>
            <span className="max-w-xs text-xs text-neutral-500 dark:text-neutral-400">
              The first image will appear here as soon as the AI generates it.
            </span>
          </div>
        )}

        {generating ? (
          <div
            role="status"
            aria-live="polite"
            data-testid="eye-preview-generating"
            // pointer-events: none keeps the underlying image clickable / inspectable.
            className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-950/55 text-neutral-50 backdrop-brightness-75"
          >
            {/* v6-polish — Plain mode swaps the spinning wheel for a
                subtle indeterminate progress bar + plain-language copy
                ("Generating… usually takes 8–15 seconds"). Technical
                mode keeps the v6 spinner + terse "Re-generating…"
                status because that's the audit-favoured surface. */}
            {lang === 'plain' ? (
              <>
                <span
                  role="progressbar"
                  aria-label="Generation progress"
                  aria-valuetext={`${elapsed} seconds elapsed`}
                  className="relative h-1.5 w-48 max-w-[60%] overflow-hidden rounded-full bg-neutral-700/70"
                >
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-accent motion-safe:animate-progress-glide"
                  />
                </span>
                <span className="text-sm font-semibold tracking-wide">
                  {`Generating… usually takes 8–15 seconds`}
                </span>
                <span className="text-[11px] text-neutral-300" aria-live="off">
                  {`${elapsed}s elapsed`}
                </span>
                <span className="sr-only">
                  {`Generating the next reference image. ${elapsed} seconds elapsed.`}
                </span>
              </>
            ) : (
              <>
                <span
                  aria-hidden="true"
                  className="h-10 w-10 motion-safe:animate-spin rounded-full border-4 border-neutral-700 border-t-accent"
                />
                <span className="text-sm font-semibold tracking-wide">
                  Re-generating…
                </span>
                <span
                  aria-hidden="true"
                  className="flex items-center gap-1 text-[11px] text-neutral-300"
                >
                  <span className="h-1.5 w-1.5 motion-safe:animate-pulse rounded-full bg-accent [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 motion-safe:animate-pulse rounded-full bg-accent [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 motion-safe:animate-pulse rounded-full bg-accent" />
                </span>
                <span className="sr-only">Generating the next reference image.</span>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* v5 — DownloadBar below the preview, hidden until the first image arrives so the layout doesn't shift. */}
      {imageId && resolved ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2 dark:border-neutral-800 dark:bg-neutral-900">
          <DownloadBar
            variant="full"
            imageId={imageId}
            title={parameters?.emotion && parameters.emotion !== 'neutral' ? `${parameters.emotion} eye` : 'Current reference'}
            hideSetAsStarting={!onSetAsStarting}
            onSetAsStarting={onSetAsStarting}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Synthesise a descriptive alt text from the current Parameters so AT
 * users hear what the eye actually looks like. Falls back to a neutral
 * description when no parameters are known yet.
 */
function describeEye(p: Parameters | null): string {
  if (!p) return 'Generated eye reference';
  const parts: string[] = [];
  parts.push('Generated eye');
  if (p.iris_color) parts.push(`iris ${p.iris_color}`);
  if (p.iris_pattern && p.iris_pattern !== 'radial') parts.push(`${p.iris_pattern} pattern`);
  if (p.pupil_shape && p.pupil_shape !== 'round') parts.push(`${p.pupil_shape.replace(/-/g, ' ')} pupil`);
  if (p.eyelid_shape && p.eyelid_shape !== 'open') parts.push(`${p.eyelid_shape}-closed eyelid`);
  if (p.catchlight?.enabled) {
    parts.push(p.catchlight.shape ? `with ${p.catchlight.shape} catchlight` : 'with catchlight');
  } else {
    parts.push('no catchlight');
  }
  if (p.reflected_scene?.enabled && p.reflected_scene.preset) {
    parts.push(`reflecting ${p.reflected_scene.preset}`);
  }
  return parts.join(', ');
}
